import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import SiteLayout from "@/components/site/SiteLayout";
import ArticleCard from "@/components/site/ArticleCard";

const CATEGORIES = [
  { slug: "politics", name: "రాజకీయం" },
  { slug: "district", name: "జిల్లా వార్తలు" },
  { slug: "agriculture", name: "వ్యవసాయం" },
  { slug: "crime", name: "క్రైమ్" },
  { slug: "cinema", name: "సినిమా/వినోదం" },
  { slug: "special", name: "ప్రత్యేక కథనాలు" },
];

export default function Home() {
  const [articles, setArticles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/articles", { params: { limit: 40 } }),
      api.get("/videos", { params: { limit: 6 } }),
    ])
      .then(([a, v]) => { setArticles(a.data || []); setVideos(v.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const hero = articles.find((a) => a.is_featured) || articles[0];
  const secondary = articles.filter((a) => a.id !== hero?.id).slice(0, 4);
  const rest = articles.filter((a) => a.id !== hero?.id && !secondary.includes(a));

  if (loading) {
    return (
      <SiteLayout>
        <div className="max-w-7xl mx-auto px-4 py-20 font-body-te text-center text-[#4A4A4A]">
          లోడ్ అవుతోంది...
        </div>
      </SiteLayout>
    );
  }

  if (!hero) {
    return (
      <SiteLayout>
        <div className="max-w-7xl mx-auto px-4 py-20 text-center" data-testid="empty-home">
          <div className="font-heading-te text-3xl mb-3">స్వాగతం</div>
          <p className="font-body-te text-[#4A4A4A] mb-6">
            Admin ఇంకా ఏ కథనం ప్రచురించలేదు. త్వరలోనే తాజా వార్తలు అందుబాటులోకి వస్తాయి.
          </p>
          <Link to="/admin/login" className="inline-block bg-[#2E7D32] text-white px-6 py-3 font-body-te">
            Admin Login
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      {/* Hero + secondary tetris grid */}
      <section className="max-w-7xl mx-auto px-4 py-8" data-testid="hero-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5" style={{ minHeight: 520 }}>
          <div className="md:col-span-8">
            <ArticleCard article={hero} variant="hero" />
          </div>
          <div className="md:col-span-4 grid grid-rows-2 gap-4 md:gap-5">
            {secondary.slice(0, 2).map((a) => (
              <div key={a.id} className="relative overflow-hidden nv-zoom min-h-[240px]">
                <Link to={`/article/${a.slug}`} data-testid={`hero-side-${a.slug}`}>
                  <img src={a.cover_image ? (a.cover_image.startsWith('http') ? a.cover_image : `${process.env.REACT_APP_BACKEND_URL}${a.cover_image}`) : "https://images.pexels.com/photos/13746639/pexels-photo-13746639.jpeg?auto=compress&h=650&w=940"} alt={a.title} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
                  <div className="absolute inset-0 nv-img-overlay" />
                  <div className="absolute inset-0 p-4 flex flex-col justify-end text-white">
                    <span className="nv-badge w-fit mb-2 text-[10px]">{CATEGORIES.find((c) => c.slug === a.category)?.name}</span>
                    <h3 className="font-heading-te text-lg md:text-xl font-bold leading-tight line-clamp-3">{a.title}</h3>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* By-category rails */}
      {CATEGORIES.map((cat) => {
        const items = articles.filter((a) => a.category === cat.slug).slice(0, 4);
        if (items.length === 0) return null;
        return (
          <section key={cat.slug} className="max-w-7xl mx-auto px-4 py-8" data-testid={`category-section-${cat.slug}`}>
            <div className="flex items-end justify-between border-b-2 border-[#2E7D32] pb-2 mb-6">
              <h2 className="font-heading-te text-2xl md:text-3xl font-bold">
                <span className="text-[#2E7D32]">▍</span> {cat.name}
              </h2>
              <Link to={`/category/${cat.slug}`} className="font-body-te text-sm text-[#2E7D32] hover:underline">
                అన్నీ చూడండి →
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {items.map((a) => <ArticleCard key={a.id} article={a} />)}
            </div>
          </section>
        );
      })}

      {/* Videos strip */}
      {videos.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-8" data-testid="videos-strip">
          <div className="flex items-end justify-between border-b-2 border-[#2E7D32] pb-2 mb-6">
            <h2 className="font-heading-te text-2xl md:text-3xl font-bold">
              <span className="text-[#2E7D32]">▍</span> వీడియో వార్తలు
            </h2>
            <Link to="/videos" className="font-body-te text-sm text-[#2E7D32] hover:underline">
              మరిన్ని →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {videos.slice(0, 3).map((v) => (
              <div key={v.id} className="border border-[#E5E4DC] bg-white" data-testid={`video-${v.id}`}>
                <div className="aspect-video">
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${v.youtube_id}`}
                    title={v.title}
                    frameBorder="0"
                    allowFullScreen
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-heading-te font-bold text-lg line-clamp-2">{v.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {rest.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-8">
          <div className="border-b-2 border-[#2E7D32] pb-2 mb-6">
            <h2 className="font-heading-te text-2xl md:text-3xl font-bold">
              <span className="text-[#2E7D32]">▍</span> మరిన్ని వార్తలు
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {rest.map((a) => <ArticleCard key={a.id} article={a} />)}
          </div>
        </section>
      )}
    </SiteLayout>
  );
}
